// The record cards both presentations share. Each card reads the household ledger and calls ledger actions; amounts
// come from the pricing engine through the records, never from words. The same card appears on a Screens page and
// beside the assistant in Chat, so a choice made in one is already there in the other.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { Case, Order, Payment } from '@roundstead/shared/ledger';
import { DEAL, FINDING } from '@roundstead/shared/ledger';
import { useStore } from './store.tsx';
import { Button, Bracket, Chip, KV, Row, cls } from './ds.tsx';
import { W, money, unitsWord } from './words.ts';
import { control, UNRESOLVED } from '../../content/content.ts';

/** Text with [bracketed unresolved fields] shown as brackets. */
export function T({ children }: { children: string }) {
  const parts = children.split(/(\[[^\]]+\])/g).filter(Boolean);
  return <>{parts.map((p, i) => (p.startsWith('[') ? <Bracket key={i}>{p}</Bracket> : <span key={i}>{p}</span>))}</>;
}
const Tone = ({ tone, children }: { tone: 'good' | 'warning' | 'critical' | 'accent' | 'neutral'; children: ReactNode }) => <span className={`hc-chip hc-chip--${tone}`}>{children}</span>;
export const TestChip = () => <Chip>{W.testPrices}</Chip>;
const Card = ({ title, chip, children, label }: { title?: ReactNode; chip?: ReactNode; children: ReactNode; label?: string }) => (
  <section className="hc-card rs-stack-8" aria-label={label}>
    {(title || chip) && <div className="rs-between">{title && <h2 className="hc-heading rs-title-sm rs-m0">{title}</h2>}{chip}</div>}
    {children}
  </section>
);

/* ——— F1: offer, context, preparation, quote, review ——— */

export function OfferPrices() {
  const { records } = useStore();
  const p = records?.pricing; if (!p) return null;
  const v = p.visit_by_units;
  return (
    <Card title={W.membership} chip={<TestChip />} label={W.testPrices}>
      <span className="rs-price"><span className="rs-price__big">{money(p.membership.per_year)}</span><span className="rs-muted">{W.perYear}</span></span>
      <span className="hc-caption">{W.includesNoVisit}</span>
      <h3 className="hc-heading rs-m0">{W.visitPrices}</h3>
      <KV rows={[[W.oneUnit, money(v['1'])], [W.twoUnits, money(v['2'])], [W.threeUnits, money(v['3'])], [W.fourPlus, W.scopedQuote]]} />
      <span className="hc-caption">{W.exampleTotals}: {money(p.membership.per_year + v['1'])} · {money(p.membership.per_year + v['2'])} · {money(p.membership.per_year + v['3'])} for one, two or three units.</span>
    </Card>
  );
}

export function DealsNow() {
  const { scenario, h } = useStore();
  const member = h?.membership.state === 'active';
  return (
    <Card title={W.availableDeals}>
      {scenario.deals === 'available'
        ? <span className="hc-caption">{DEAL.title} · {member ? 'available to members' : 'available after joining'} · optional</span>
        : <span className="hc-caption">{W.noDeals}</span>}
    </Card>
  );
}

export function RoutineHelp() { return <p className="hc-caption rs-m0"><T>{W.routineHelp}</T></p>; }

export function UnresolvedList() {
  return (
    <div className="rs-stack-8">
      <p className="rs-m0">{W.notDecidedLead}</p>
      <KV rows={UNRESOLVED.map((u) => [u.decision, <Bracket>[unresolved]</Bracket>] as [string, ReactNode])} />
    </div>
  );
}

const UNIT_CHOICES = [[1, W.oneUnit], [2, W.twoUnits], [3, W.threeUnits], ['four_plus', W.fourPlus], ['unknown', W.unknownUnits]] as const;

/** F1 View 2: the household's own count, photos optional, and what photos suggest (never inventory). */
export function UnitsCard() {
  const { h, records, act, busy } = useStore();
  const reported = h?.units.reported ?? null;
  const suggested = records?.basis.photoSuggested ?? null;
  return (
    <Card title={W.unitsQuestion}>
      <fieldset className="rs-fieldset">
        <legend className="rs-sr">{W.unitsQuestion}</legend>
        <div className="rs-seg" role="radiogroup" aria-label={W.unitsQuestion}>
          {UNIT_CHOICES.map(([v, label]) => (
            <button key={String(v)} type="button" role="radio" aria-checked={reported === v} className="rs-seg__btn rs-press" disabled={!!busy}
                    onClick={() => act({ type: 'report_units', value: v })}>{label}</button>
          ))}
        </div>
      </fieldset>
      <span className="hc-caption"><T>{W.unitsHelp}</T></span>
      {reported && <span className="hc-caption">{unitsWord(reported)} · {W.reported}</span>}
      {!reported && <span className="hc-caption">{W.membershipOnlyNote}</span>}
      {suggested && reported !== suggested && (
        <div className="rs-stack-4">
          <span className="hc-caption">{W.photoSuggestion(suggested)}</span>
          {suggested <= 3 && <Button kind="quiet" className="rs-auto rs-start" onClick={() => act({ type: 'confirm_photo_suggestion', suggested })}>{W.useSuggestion(suggested)}</Button>}
        </div>
      )}
    </Card>
  );
}

export function HomeCard() {
  return <Card><KV rows={[[W.home, W.homeValue], [W.eligibility, <T>{W.eligibilityValue}</T>]]} /></Card>;
}

export function PhotoList({ removable }: { removable?: boolean }) {
  const { photos, retry, removePhoto, records, serverId } = useStore();
  if (!photos.length) return null;
  const unclear = new Set(records?.facts?.unclear_photos ?? []); const stopped = new Set(records?.stopped ?? []);
  const note = (id?: string) => (id && stopped.has(id) ? W.stoppedPhoto : id && unclear.has(id) ? W.unclearPhoto : W.received);
  return (
    <ul className="rs-photos" aria-label={W.photos}>
      {photos.map((p) => (
        <li key={p.id} className={cls('rs-photo', p.state === 'failed' && 'rs-photo--failed')}>
          <span className="rs-photo__thumb" style={p.preview ? { backgroundImage: `url(${p.preview})` } : undefined} aria-hidden="true" />
          <span className="rs-photo__body">
            <span className="rs-photo__name">{p.name}</span>
            <span className={cls('rs-photo__state', p.state === 'uploading' && 'rs-shimmer')}>{p.state === 'received' ? note(serverId(p)) : p.state === 'uploading' ? W.uploading : W.failed}</span>
          </span>
          {p.state === 'failed' && <span className="rs-photo__acts"><Button kind="quiet" className="rs-auto" onClick={() => retry(p.id)}>{W.retry}</Button><Button kind="quiet" className="rs-auto" onClick={() => removePhoto(p.id)}>{W.remove}</Button></span>}
          {p.state === 'received' && removable && <span className="rs-photo__acts"><Button kind="quiet" className="rs-auto" onClick={() => removePhoto(p.id)}>{W.remove}</Button></span>}
        </li>
      ))}
    </ul>
  );
}

export function PhotoButtons({ pick }: { pick: () => void }) {
  const { busy, openExc } = useStore();
  return (
    <div className="rs-stack-8">
      <span className="hc-caption">{W.photosOptional}</span>
      <Button onClick={pick} disabled={!!busy}>{control(1, 2, 0)}</Button>
      <Button kind="quiet" className="rs-auto rs-start" onClick={() => openExc('photo-skipped')}>{W.cantGetPhoto}</Button>
    </div>
  );
}

/** F1 View 3: preparation, with the scoped-review notice for four or more units or an unknown count. */
export function PreparingCard() {
  const { h, records } = useStore();
  const scoped = records?.basis.status === 'scoped_review';
  return (
    <Card title={W.infoReceived} chip={<Tone tone="accent">BOB is handling this</Tone>}>
      <span className="hc-caption">{W.preparingNote}</span>
      <KV rows={[[W.owner, <T>{W.ownerValue}</T>], [W.nextUpdate, <T>{W.nextUpdateValue}</T>], ['Units', `${unitsWord(h?.units.reported)} · reported`]]} />
      {scoped && <p className="rs-m0">{W.scopedReview}</p>}
    </Card>
  );
}

/** F1 View 4 (and a member's visit request): every line with its amount, the visit optional, the exact total. */
export function QuoteCard() {
  const { records, h, act, busy, setSheet } = useStore();
  const p = records?.purchase; if (!p) return null;
  const visitLine = p.quote.lines.find((l) => l.kind === 'visit');
  const include = h?.includeVisit ?? true;
  return (
    <Card title={`${W.quote} ${p.quote.ref} · ${p.quote.version} · ${p.quote.terms}`} chip={<TestChip />} label={W.quote}>
      {p.quote.lines.map((l) => {
        const chosen = p.lines.includes(l);
        return (
          <div key={l.kind} className={cls('rs-option', 'hc-card', chosen && 'hc-card--selected')}>
            <span className="rs-between"><span className="hc-heading">{l.label}</span><span className="rs-price__big">{l.amount === null ? '—' : money(l.amount)}</span></span>
            <span className="hc-caption">{l.note}</span>
            {l.kind === 'visit' && p.quote.kind === 'join' && <span className="hc-caption">{W.visitOptional}</span>}
          </div>
        );
      })}
      {visitLine && p.quote.kind === 'join' && (
        <Button kind="quiet" className="rs-auto rs-start" disabled={!!busy} aria-pressed={include}
                onClick={() => act({ type: 'include_visit', include: !include })}>{include ? W.removeVisit : W.addVisit}</Button>
      )}
      <div className="rs-between"><span className="hc-strong">{W.total}</span>{p.total === null ? <span className="hc-strong">{W.noTotal}</span> : <span className="rs-price__big">{money(p.total)}</span>}</div>
      {p.total === null && p.quote.kind === 'join' && <span className="hc-caption">{W.removeToJoin}</span>}
      <span className="hc-caption"><T>{W.beforeCharges}</T></span>
      <Row label={control(1, 4, 2)} value={<Bracket>[unresolved]</Bracket>} onClick={() => setSheet('terms')} />
    </Card>
  );
}

export function TermsRows() {
  return (
    <KV rows={[[W.tasks, <Bracket>[unresolved]</Bracket>], [W.unitDefinition, <Bracket>[unresolved]</Bracket>], [W.paymentTiming, <Bracket>[unresolved]</Bracket>],
               [W.cancellation, <Bracket>[unresolved]</Bracket>], [W.activation, <Bracket>[unresolved]</Bracket>], ['Applicable taxes or other charges', <Bracket>[unresolved]</Bracket>]]} />
  );
}

/** F1 View 5: the exact commitment, separate items, separate choices. */
export function ReviewCard() {
  const { records } = useStore();
  const p = records?.purchase; if (!p) return null;
  return (
    <Card title={W.exactCommitment} chip={<TestChip />}>
      <KV rows={[...p.lines.map((l) => [`${l.label} · ${l.note}`, money(l.amount)] as [string, string]), [W.total, p.total === null ? W.scopedQuote : money(p.total)],
                 ['Version', `${p.quote.ref} ${p.quote.version} · ${p.quote.terms}`]]} />
      <span className="hc-caption">{W.separateAuthority}</span>
      <span className="hc-caption">{W.noPurchaseYet}</span>
    </Card>
  );
}

export function InfoChoices() {
  const { h, act, busy } = useStore();
  if (!h) return null;
  const one = (key: 'model' | 'offers', label: string) => {
    const c = h.info[key];
    return (
      <label className="hc-toggle rs-toggle">
        <span className="rs-stack-4"><span>{label}</span>
          <span className="hc-caption">{c.state === 'initial' ? <T>Initial setting [unresolved]</T> : c.state === 'pending' ? 'Requested · pending downstream' : 'Applied downstream'}</span></span>
        <input type="checkbox" className="hc-switch" checked={c.off} disabled={!!busy} onChange={(e) => act({ type: 'set_choice', key, off: e.target.checked })} />
      </label>
    );
  };
  return (
    <Card title={W.infoChoices}>
      <p className="rs-m0">{W.visibility} <Chip>{W.proposedWording}</Chip></p>
      {one('model', W.optModel)}{one('offers', W.optOffers)}
      <span className="hc-caption">{W.separateChoices}</span>
    </Card>
  );
}

// The research card. Any other number that could be a real card is refused, and nothing typed is kept or sent.
const RESEARCH = { number: '4242 4242 4242 4242', expiry: '12/30', cvc: '123', name: 'Research household', zip: '27513' };
type Fields = typeof RESEARCH;
const luhn = (d: string) => { let sum = 0; for (let i = 0; i < d.length; i++) { let n = +d[d.length - 1 - i]; if (i % 2) { n *= 2; if (n > 9) n -= 9; } sum += n; } return sum % 10 === 0; };
export function cardErrors(f: Fields, now = new Date()): Partial<Record<keyof Fields, string>> {
  const e: Partial<Record<keyof Fields, string>> = {};
  const digits = f.number.replace(/\D/g, '');
  if (digits !== RESEARCH.number.replace(/\D/g, '')) e.number = digits.length >= 12 && digits.length <= 19 && luhn(digits) ? W.realCard : W.badCard;
  const m = f.expiry.match(/^(\d{2})\s*\/?\s*(\d{2})$/);
  if (!m || +m[1] < 1 || +m[1] > 12 || (2000 + +m[2]) * 12 + +m[1] < now.getFullYear() * 12 + now.getMonth() + 1) e.expiry = W.badExpiry;
  if (!/^\d{3}$/.test(f.cvc)) e.cvc = W.badCvc;
  if (!f.name.trim()) e.name = W.badName;
  if (!/^\d{5}$/.test(f.zip)) e.zip = W.badZip;
  return e;
}

/** Simulated secure payment fields and the exact purchase control. Payment details never enter the conversation. */
export function PaymentForm({ total, onPay, label }: { total: number | null; onPay: () => Promise<void>; label?: string }) {
  const { setAnnounce, acceptPing } = useStore();
  const [f, setF] = useState<Fields>({ number: '', expiry: '', cvc: '', name: '', zip: '' });
  const [errs, setErrs] = useState<ReturnType<typeof cardErrors>>({});
  const [working, setWorking] = useState(false);
  const form = useRef<HTMLFormElement>(null);
  useEffect(() => {
    if (Date.now() - acceptPing > 1500) return;
    const b = form.current?.querySelector<HTMLButtonElement>('button[type=submit]');
    setTimeout(() => { b?.scrollIntoView({ behavior: 'smooth', block: 'center' }); b?.focus({ preventScroll: true }); }, 350);
  }, [acceptPing]);
  const set = (k: keyof Fields, v: string) => { setF({ ...f, [k]: k === 'number' ? v.replace(/[^\d]/g, '').slice(0, 19).replace(/(\d{4})(?=\d)/g, '$1 ') : v }); if (errs[k]) setErrs({ ...errs, [k]: undefined }); };
  const field = (k: keyof Fields, lbl: string, mode: 'numeric' | 'text', ph?: string) => (
    <label className={cls('hc-field', errs[k] && 'hc-field--error')}>
      <span className="hc-label">{lbl}</span>
      <input className="hc-input" name={`rs-${k}`} value={f[k]} inputMode={mode} autoComplete="off" autoCorrect="off" spellCheck={false} placeholder={ph}
             aria-invalid={!!errs[k]} aria-describedby={errs[k] ? `e-${k}` : undefined} onChange={(e) => set(k, e.target.value)} />
      {errs[k] && <span id={`e-${k}`} className="hc-error" role="alert">{errs[k]}</span>}
    </label>
  );
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); if (working || total === null) return;
    const found = cardErrors(f); setErrs(found);
    const first = Object.keys(found)[0];
    if (first) { form.current?.querySelector<HTMLInputElement>(`[name="rs-${first}"]`)?.focus(); return; }
    setWorking(true); setAnnounce(W.processing);
    await new Promise((r) => setTimeout(r, 700));
    setF({ number: '', expiry: '', cvc: '', name: '', zip: '' });
    try { await onPay(); } finally { setWorking(false); }
  };
  return (
    <form ref={form} className="rs-stack-12" onSubmit={submit} noValidate>
      <section className="hc-card rs-stack-12">
        <div className="rs-between"><h2 className="hc-heading rs-m0">{W.paymentTitle}</h2><span className="hc-caption">{W.secure}</span></div>
        {field('number', W.cardNumber, 'numeric')}
        <div className="rs-two">{field('expiry', W.expiry, 'numeric', 'MM/YY')}{field('cvc', W.securityCode, 'numeric')}</div>
        {field('name', W.nameOnCard, 'text')}
        {field('zip', W.zip, 'numeric')}
        <div className="rs-between rs-research"><span className="hc-caption">{W.noCard}</span>
          <Button kind="quiet" className="rs-auto" onClick={() => { setF(RESEARCH); setErrs({}); }}>{W.useResearchCard}</Button></div>
      </section>
      <div className="rs-stack-8">
        <button type="submit" className={cls('hc-btn hc-btn--primary rs-press', working && 'rs-working')} disabled={working || total === null}>
          {working ? W.processing : total === null ? W.cannotAccept : (label ?? W.simulateAcceptance(total))}</button>
        <p className="hc-caption rs-center">{W.simulated}</p>
      </div>
    </form>
  );
}

/* ——— Outcomes and membership ——— */

const PAY_TONE: Record<Payment['state'], 'good' | 'warning' | 'critical' | 'neutral'> = { confirmed: 'good', processing: 'neutral', failed: 'critical', unknown: 'warning' };
const PAY_WORD: Record<Payment['state'], string> = { confirmed: 'Confirmed', processing: 'Processing', failed: 'Failed', unknown: 'Outcome unknown' };

export function ReceiptCard({ payment }: { payment: Payment }) {
  return (
    <Card title={payment.state === 'confirmed' ? W.paymentConfirmed : PAY_WORD[payment.state]} chip={<Tone tone={PAY_TONE[payment.state]}>{PAY_WORD[payment.state]}</Tone>}>
      <KV rows={[[W.amount, money(payment.amount)], [W.paidItems, payment.lines.join(' + ')], [W.attempt, payment.id], ...(payment.receipt ? [[W.receipt, `${payment.receipt} · ${W.fictional.toLowerCase()}`]] as [string, string][] : [])]} />
      <span className="hc-caption">{W.confirmedNote}</span>
    </Card>
  );
}

const MEMBERSHIP_WORD = { none: 'Not a member', payment_pending: 'Waiting on payment outcome', activation_pending: 'Paid · activation pending', active: 'Active', activation_problem: 'Paid · activation problem' } as const;

export function MembershipCard() {
  const { h } = useStore();
  if (!h) return null;
  const m = h.membership;
  return (
    <Card title={`${W.membershipLabel} ${m.id}`} chip={<Tone tone={m.state === 'active' ? 'good' : m.state === 'activation_problem' ? 'critical' : 'neutral'}>{MEMBERSHIP_WORD[m.state]}</Tone>}>
      <KV rows={[[W.amount, m.fee ? `${money(m.fee)} ${W.perYear}` : '—'], [W.term, <T>{W.termValue}</T>], [W.benefits, <T>{W.benefitsValue}</T>],
                 ...(m.exit.state === 'requested' ? [['Leaving', 'Requested']] as [string, string][] : [])]} />
      <span className="hc-caption">{W.noIncludedVisits}</span>
    </Card>
  );
}

const BOOKING_WORD: Record<Order['booking'], string> = {
  not_requested: 'No appointment requested', requested: 'Requested · not confirmed', windows_offered: 'Windows offered · not confirmed', confirmed: 'Appointment confirmed',
  window_taken: 'That window was taken · not confirmed', no_capacity: 'No window available yet · BOB owns the next arrangement', change_needed: 'No longer confirmed · BOB owns rearranging it',
};

export function orderStatus(o: Order): string {
  if (o.result === 'qualified_confirmed') return o.remaining.length ? `${W.episodeClosed} · ${W.itemsRemain(o.remaining.length)}` : W.episodeClosed;
  if (o.result === 'disputed') return 'Result disputed · case open';
  if (o.attendance === 'absent') return 'Attendance not verified · case open';
  if (o.attendance === 'verified') return 'Attended · results pending';
  return BOOKING_WORD[o.booking];
}

export function OrdersList() {
  const { h, openPage } = useStore();
  if (!h) return null;
  return (
    <Card title={W.yourVisits}>
      {h.orders.length ? h.orders.map((o) => (
        <Row key={o.id} label={<span className="rs-stack-4"><span>{o.id} · {unitsWord(o.units)} · {money(o.price)}</span><span className="hc-caption">{orderStatus(o)}</span></span>}
             value={null} onClick={() => openPage(`visit:${o.id}`)} />
      )) : <span className="hc-caption">{W.noVisits}</span>}
    </Card>
  );
}

export function OpenItems() {
  const { records } = useStore();
  const c = records?.commitments ?? [];
  if (!c.length) return null;
  return <Card title={W.openItems}><ul className="hc-list">{c.map((x, i) => <li key={i} className="hc-list__row">{x.text}</li>)}</ul></Card>;
}

/* ——— F2 and F3: a purchased visit ——— */

const PREFS = ['Mornings', 'Afternoons', 'Afternoons except Thursday', 'Any weekday'];

export function VisitDetail({ id }: { id: string }) {
  const { h, act, busy, contact, openPage } = useStore();
  const o = h?.orders.find((x) => x.id === id);
  const [pref, setPref] = useState<string | null>(o?.preference ?? null);
  if (!h || !o) return <p className="hc-caption">No such visit.</p>;
  const pay = h.payments.find((p) => p.id === o.paymentId);
  const confirmedBooking = o.booking === 'confirmed';
  return (
    <div className="rs-stack-12">
      <Card title={`${W.order} ${o.id} · ${unitsWord(o.units)}`} chip={<TestChip />}>
        <KV rows={[[W.paid, `${money(o.price)} · ${pay?.receipt ?? pay?.state ?? '—'}`], [W.assignment, o.assignment === 'accepted' ? 'Accepted by a qualified technician [fictional]' : o.assignment === 'unavailable' ? 'No technician available · BOB owns the next arrangement' : o.assignment === 'requested' ? 'Requested · not yet accepted' : 'Not yet requested'],
                   ['Status', orderStatus(o)]]} />
        {o.scopeNote && <span className="hc-caption">{o.scopeNote}</span>}
      </Card>

      <Card title={W.preference}>
        <span className="hc-caption">{W.preferenceHelp}</span>
        <div className="rs-wrap">{PREFS.map((p) => <button key={p} type="button" className="rs-seg__btn rs-press" aria-pressed={pref === p} onClick={() => setPref(p)}>{p}</button>)}</div>
        <Button kind="quiet" className="rs-auto rs-start" disabled={!pref || pref === o.preference || !!busy} onClick={() => act({ type: 'set_preference', orderId: o.id, text: pref! })}>{control(2, 2, 0)}</Button>
        {o.preference && <span className="hc-caption">Saved: {o.preference}</span>}
      </Card>

      <Card title={W.access} chip={<Tone tone={o.access === 'granted' ? 'good' : 'neutral'}>{o.access === 'granted' ? 'Granted' : o.access === 'revoked' ? 'Revoked' : 'Not given'}</Tone>}>
        <span className="hc-caption">{W.accessHelp}</span>
        <Button kind="quiet" className="rs-auto rs-start" disabled={!!busy} onClick={() => act({ type: 'set_access', orderId: o.id, granted: o.access !== 'granted' })}>{o.access === 'granted' ? W.revokeAccess : W.grantAccess}</Button>
      </Card>

      <Card title={W.window} chip={<Tone tone={confirmedBooking ? 'good' : 'neutral'}>{BOOKING_WORD[o.booking]}</Tone>}>
        {confirmedBooking && <span className="rs-big">{o.appointment} <Chip>{W.fictionalWindow}</Chip></span>}
        {(o.booking === 'windows_offered' || o.booking === 'window_taken') && o.windows.map((w) => (
          <div key={w} className="rs-between"><span>{w} <Chip>{W.fictional}</Chip></span><Button kind="secondary" className="rs-auto" disabled={!!busy} onClick={() => act({ type: 'choose_window', orderId: o.id, window: w })}>{W.chooseWindow}</Button></div>
        ))}
        {o.attendance === 'not_yet' && (o.booking === 'not_requested' || o.booking === 'no_capacity' || o.booking === 'change_needed') &&
          <Button kind="primary" disabled={!!busy} onClick={() => act({ type: 'request_booking', orderId: o.id })}>{W.requestWindows}</Button>}
        {confirmedBooking && o.attendance === 'not_yet' && <Button kind="quiet" className="rs-auto rs-start" disabled={!!busy} onClick={() => act({ type: 'change_booking', orderId: o.id })}>{control(2, 3, 2)}</Button>}
        <span className="hc-caption"><T>Member-chosen timing: request whenever you like. Only an accepted window confirms an appointment. Cancellation and no-access terms are [unresolved].</T></span>
      </Card>

      <Card title={W.preparation}>
        <KV rows={[['Package', <T>{W.packageValue}</T>], [W.attendance, o.attendance === 'verified' ? 'Verified' : o.attendance === 'absent' ? 'Not verified · BOB owns recovery' : 'Not yet']]} />
      </Card>

      <Card title={W.results} chip={o.result === 'qualified_confirmed' ? <Tone tone="good">Qualified result</Tone> : o.result === 'disputed' ? <Tone tone="warning">Disputed</Tone> : undefined}>
        {o.items.every((i) => i.result === 'not_started') ? <span className="hc-caption">{W.resultNone}</span> : (
          <ul className="hc-list">{o.items.map((i) => (
            <li key={i.id} className="hc-list__row"><span className="rs-stack-4"><span><T>{i.label}</T></span>{i.limitation && <span className="hc-caption">{W.limitation}: <T>{i.limitation}</T></span>}</span>
              <span className="hc-list__value">{i.result.replace('_', ' ')}</span></li>))}</ul>
        )}
        {o.result === 'qualified_confirmed' && <KV rows={[[W.evidence, <T>{W.evidenceValue}</T>], ['Confirmed by', <T>[qualified technician, fictional]</T>]]} />}
        <div className="rs-row-actions">
          {(o.attendance !== 'not_yet' || o.result !== 'none') && o.result !== 'disputed' && <Button kind="quiet" className="rs-auto" disabled={!!busy} onClick={() => act({ type: 'dispute', orderId: o.id })}>{control(2, 6, 2)}</Button>}
          <Button kind="quiet" className="rs-auto" disabled={!!busy} onClick={() => act({ type: 'report_problem', orderId: o.id, text: null })}>{control(2, 5, 0)}</Button>
        </div>
      </Card>

      {o.remaining.length > 0 && (
        <Card title={W.remaining}>
          <ul className="hc-list">{o.remaining.map((r, i) => <li key={i} className="hc-list__row"><span className="rs-stack-4"><T>{r.item}</T><span className="hc-caption"><T>{r.action}</T></span></span><span className="hc-list__value">{r.owner === 'accepted' ? W.ownerAccepted : W.ownerRequested}</span></li>)}</ul>
          <span className="hc-caption">Closing the visit episode does not discharge these. They remain owed if you leave.</span>
        </Card>
      )}

      {o.finding && <FindingCard order={o} />}

      <div className="rs-row-actions">
        <Button kind="quiet" className="rs-auto" onClick={() => openPage('request')}>{control(2, 7, 1)}</Button>
        <Button kind="quiet" className="rs-auto" onClick={() => contact(o.id)}>{control(2, 8, 0)}</Button>
      </div>
      <CasesFor about={o.id} />
    </div>
  );
}

/** F3: a qualified finding and its separate proposal. Approval is specific; declining keeps the original care. */
export function FindingCard({ order }: { order: Order }) {
  const { act, busy } = useStore();
  const f = order.finding!;
  return (
    <Card title={W.finding} chip={<Chip>{W.fictional}</Chip>}>
      <KV rows={[['Source', <T>[qualified technician, fictional · dated finding]</T>], [W.proposal, <T>{f.scope}</T>],
                 [W.amount, f.complete && f.amount !== null ? <span className="rs-stack-4"><span>{money(f.amount)}</span><span className="hc-caption">{FINDING.label}</span></span> : <T>[price and terms not yet complete]</T>],
                 ['Decision', f.decision === 'unapproved' ? 'Not decided' : f.decision === 'approved' ? 'Approved (simulated) · payment and booking are separate' : f.decision === 'declined' ? 'Declined' : 'Qualified review requested']]} />
      {!f.complete && <span className="hc-caption">{W.proposalIncomplete}</span>}
      <span className="hc-caption">{W.originalCare} Urgency and safety are a qualified person's judgment, not the assistant's.</span>
      {f.decision === 'unapproved' && (
        <div className="rs-stack-8">
          {f.complete && f.amount !== null && <Button kind="primary" disabled={!!busy} onClick={() => act({ type: 'decide_finding', orderId: order.id, decision: 'approve', amount: f.amount! })}>{W.approveSim(f.amount)}</Button>}
          <div className="rs-row-actions">
            <Button kind="quiet" className="rs-auto" disabled={!!busy} onClick={() => act({ type: 'decide_finding', orderId: order.id, decision: 'decline' })}>{W.declineWork}</Button>
            <Button kind="quiet" className="rs-auto" disabled={!!busy} onClick={() => act({ type: 'decide_finding', orderId: order.id, decision: 'review' })}>{W.requestReview}</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

/* ——— F4: cases ——— */

const CASE_KIND: Record<Case['kind'], string> = { person: 'Help from a person', visit_problem: 'Problem with purchased care', payment_reconciliation: 'Payment being reconciled', refund_review: 'Review of money after leaving', information: 'Information request' };
const OWNER_WORD: Record<Case['owner'], string> = { requested: W.ownerRequested, accepted: W.ownerAccepted, unavailable: W.ownerUnavailable };
const CASE_STATE: Record<Case['state'], string> = { open: 'Open', remedy_pending: 'Remedy being arranged', remedy_done: 'Remedy recorded · you can reopen', closed: 'Closed', reopened: 'Reopened' };

export function CaseCard({ k }: { k: Case }) {
  const { act, busy } = useStore();
  return (
    <Card title={`${W.case} ${k.id} · ${CASE_KIND[k.kind]}`} chip={<Tone tone={k.owner === 'accepted' ? 'good' : k.owner === 'unavailable' ? 'warning' : 'neutral'}>{OWNER_WORD[k.owner]}</Tone>}>
      <KV rows={[[W.received, k.about ? `About ${k.about}` : 'Yes'], ['State', CASE_STATE[k.state]], [W.owner, k.owner === 'accepted' ? <T>[accepted owner] · BOB service team · person</T> : OWNER_WORD[k.owner]], [W.nextUpdate, <T>[only if actually promised]</T>]]} />
      {k.text && <span className="hc-caption">{k.text}</span>}
      <span className="hc-caption">{W.attachedNote}</span>
      {(k.state === 'remedy_done' || k.state === 'closed') && <Button kind="quiet" className="rs-auto rs-start" disabled={!!busy} onClick={() => act({ type: 'reopen_case', caseId: k.id })}>{W.reopen}</Button>}
    </Card>
  );
}

export function CasesFor({ about }: { about?: string }) {
  const { h } = useStore();
  const ks = (h?.cases ?? []).filter((k) => about === undefined || k.about === about);
  return <>{ks.map((k) => <CaseCard key={k.id} k={k} />)}</>;
}

/* ——— F5: deals and benefits ——— */

export function DealsCard() {
  const { h, scenario, act, busy } = useStore();
  if (!h) return null;
  const declined = h.deals.declined.includes(DEAL.id);
  const order = h.deals.orders.at(-1);
  const total = DEAL.goods + DEAL.delivery;
  return (
    <div className="rs-stack-12">
      <Card title="Current value">
        <KV rows={[[W.membershipLabel, h.membership.fee ? `${money(h.membership.fee)} ${W.perYear}` : '—'], [W.yourVisits, String(h.orders.length)], [W.benefits, <T>{W.benefitsValue}</T>]]} />
        <span className="hc-caption">Only deals available now count. No saving is promised.</span>
      </Card>
      {scenario.deals !== 'available' ? <Card title={W.availableDeals}><span className="hc-caption">{W.noDeals}</span></Card> : (
        <Card title={DEAL.title} chip={<Chip>{W.fictional}</Chip>}>
          <KV rows={[['Supplier', <T>{DEAL.supplier}</T>], ['Fit', <T>{DEAL.fit}</T>], [W.goods, money(DEAL.goods)], [W.delivery, money(DEAL.delivery)], [W.total, money(total)]]} />
          <span className="hc-caption">{DEAL.terms}. Optional: declining changes nothing else.</span>
          {order ? <span className="hc-strong">{order.state === 'authorized' ? 'Authorized · waiting for the supplier to accept · nothing charged in this simulation' : order.state === 'supplier_accepted' ? 'Supplier accepted · fulfillment not yet confirmed' : 'Supplier declined · nothing charged · BOB owns any alternative [unresolved]'}</span>
            : declined ? <span className="hc-caption">Declined. You won't be asked again for this deal.</span>
            : <div className="rs-stack-8">
                <Button kind="primary" disabled={!!busy || h.membership.state !== 'active'} onClick={() => act({ type: 'deal_order', dealId: DEAL.id, total })}>{W.orderDeal(total)}</Button>
                <Button kind="quiet" className="rs-auto rs-start" disabled={!!busy} onClick={() => act({ type: 'deal_decline', dealId: DEAL.id })}>{W.notInterested}</Button>
              </div>}
        </Card>
      )}
      <Card title={W.earned}><span className="hc-caption"><T>{W.earnedNone}</T></span></Card>
    </div>
  );
}

/* ——— F6: records and choices ——— */

export function RecordsCards() {
  const { h, records, act, busy } = useStore();
  if (!h) return null;
  const hist = h.units.history;
  return (
    <div className="rs-stack-12">
      <Card title="Home records">
        <KV rows={[[W.home, W.homeValue], [W.unitsHistory, `${unitsWord(h.units.reported)} · reported, not verified`],
                   [W.photoSuggested, records?.basis.photoSuggested ? `${records.basis.photoSuggested} outdoor · a suggestion` : 'None'], [W.photos, String(records?.photos.length ?? 0)]]} />
        {hist.length > 0 && <ul className="hc-list">{hist.map((x, i) => <li key={i} className="hc-list__row"><span>{unitsWord(x.value)}</span><span className="hc-list__value">{x.source === 'household' ? 'You reported' : 'You confirmed a photo suggestion'}</span></li>)}</ul>}
        <span className="hc-caption">These dated records are not continuous observation or a safety judgment.</span>
      </Card>
      {h.info.correction && (
        <Card title="Correction">
          <KV rows={[['Change', `${unitsWord(h.info.correction.from)} → ${unitsWord(h.info.correction.to)}`], [W.downstream, h.info.correction.downstream === 'applied' ? 'Applied' : 'Pending · qualified review']]} />
          <span className="hc-caption">A reported change never reprices an accepted visit silently.</span>
        </Card>
      )}
      <Card title="Who can see what">
        <p className="rs-m0">{W.visibility} <Chip>{W.proposedWording}</Chip></p>
        <span className="hc-caption"><T>Roles, retention and member visibility are [unresolved]. Information choices never authorize spending.</T></span>
      </Card>
      <InfoChoices />
      <Card title={W.exportResult}>
        <span className="hc-caption">{h.info.export === 'none' ? 'Not requested.' : h.info.export === 'available' ? 'Generated and available to download [fictional file]. Sending it anywhere is a separate choice.' : 'Generated but not accessible. BOB owns fixing it; the request is kept.'}</span>
        <Button kind="quiet" className="rs-auto rs-start" disabled={!!busy} onClick={() => act({ type: 'request_export' })}>{W.exportRecords}</Button>
      </Card>
      <Card title={W.deletionResult}>
        {h.info.deletion === 'partial'
          ? <KV rows={[['Photos in the app', 'Deleted [simulated]'], ['Quote and purchase records', 'Retained [retention rule unresolved]'], ['Backups', 'Pending [rule unresolved]']]} />
          : <span className="hc-caption">Not requested. A request shows what was deleted, kept under a stated rule, or still pending.</span>}
        {h.info.deletion === 'none' && <Button kind="quiet" className="rs-auto rs-start" disabled={!!busy} onClick={() => act({ type: 'request_deletion' })}>{W.deleteRecords}</Button>}
      </Card>
    </div>
  );
}

/* ——— F7: renewal and leaving ——— */

export function RenewalCards() {
  const { h, records, act, busy } = useStore();
  if (!h) return null;
  const m = h.membership; const r = m.renewal;
  const renewalPay = h.payments.find((p) => p.for === 'renewal');
  const owed = records?.commitments ?? [];
  return (
    <div className="rs-stack-12">
      <MembershipCard />
      <Card title={W.nextTerm} chip={r.state === 'offered' ? <TestChip /> : undefined}>
        {r.state === 'not_offered' && <span className="hc-caption">{W.nextTermNone}</span>}
        {r.state === 'offered' && (
          <>
            <KV rows={[['Offer', r.version!], [W.amount, `${money(r.amount)} ${W.perYear}`], [W.benefits, <T>[next-term benefits unresolved]</T>], ['Changes', <T>[material changes, if any]</T>]]} />
            <span className="hc-caption">The test price is not guaranteed for later terms. Renewing never orders or books a visit.</span>
            <Button kind="primary" disabled={!!busy} onClick={async () => { await act({ type: 'renew', version: r.version!, amount: r.amount! }); }}>{W.approveRenewal(r.amount!)}</Button>
            <Button kind="quiet" className="rs-auto rs-start" disabled={!!busy} onClick={() => act({ type: 'renew_decline' })}>{W.dontRenew}</Button>
          </>
        )}
        {r.state === 'consented' && <span className="hc-caption">Approved for {r.version} · payment {renewalPay ? `${PAY_WORD[renewalPay.state].toLowerCase()}${renewalPay.receipt ? `, ${renewalPay.receipt}` : ''}` : 'not started'} · next-term activation [rule unresolved]</span>}
        {r.state === 'declined' && <span className="hc-caption">You declined the next term. No renewal charge will be authorized.</span>}
      </Card>
      <Card title={m.exit.state === 'requested' ? 'Leaving requested' : W.consequences}>
        <KV rows={[[W.accessEnds, <T>{W.accessEndsValue}</T>], [W.billingLabel, m.exit.state === 'requested' ? (m.billing === 'stop_confirmed' ? 'Stop confirmed' : 'Stop requested · not yet confirmed') : 'Unchanged'],
                   [W.refundLabel, <T>{`${W.refundValue} [unresolved]`}</T>]]} />
        <h3 className="hc-heading rs-m0">{W.stillOwed}</h3>
        {owed.length ? <ul className="hc-list">{owed.map((x, i) => <li key={i} className="hc-list__row">{x.text}</li>)}</ul> : <span className="hc-caption">{W.nothingOwed}</span>}
        <span className="hc-caption">{W.leaveNote} Purchased work, open cases and earned commitments stay owed.</span>
        {m.exit.state === 'none' && m.state !== 'none' && <Button disabled={!!busy} onClick={() => act({ type: 'leave' })}>{control(7, 5, 0)}</Button>}
        {m.exit.state === 'requested' && !h.cases.some((k) => k.kind === 'refund_review') && <Button kind="quiet" className="rs-auto rs-start" disabled={!!busy} onClick={() => act({ type: 'refund_review' })}>{control(7, 6, 1)}</Button>}
      </Card>
      {h.cases.filter((k) => k.kind === 'refund_review').map((k) => <CaseCard key={k.id} k={k} />)}
    </div>
  );
}
